import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { AccountListItem } from "../../lib/accounts";
import {
  type CreateInvestmentInput,
  type InvestmentCategory,
  type InvestmentDetail,
  type InvestmentKind,
  type Portfolio,
  type UpdateInvestmentInput,
  isBalanceInvestmentKind,
} from "../../lib/investments";

const KINDS: InvestmentKind[] = [
  "AKTIE",
  "ETF",
  "KRYPTO",
  "ANLEIHE",
  "ROHSTOFFE",
  "P2P",
  "BETEILIGUNG",
  "SONSTIGE",
];
const CURRENCIES: CreateInvestmentInput["currency"][] = ["EUR", "USD", "GBP", "CHF", "JPY"];

const KNOWN_P2P_SYMBOLS = ["MONEFIT_SMARTSAVER"] as const;

function initialP2pChoice(symbol: string | undefined): string {
  if (!symbol) return "MONEFIT_SMARTSAVER";
  const known = KNOWN_P2P_SYMBOLS.find((s) => s === symbol);
  return known ?? "OTHER";
}

interface Props {
  mode: "create" | "edit";
  initial?: InvestmentDetail;
  accounts: AccountListItem[];
  onSubmit: (input: CreateInvestmentInput | UpdateInvestmentInput) => Promise<void>;
  submitting?: boolean;
  serverError?: string;
}

function parseEuroDe(input: string): number {
  const trimmed = input.replace(/\./g, "").replace(",", ".").trim();
  const n = Number.parseFloat(trimmed);
  if (Number.isNaN(n) || n <= 0) throw new Error("invalid_amount");
  return Math.round(n * 100);
}

function formatEuroDe(cents: number): string {
  return (cents / 100).toLocaleString("de-DE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function todayIso(): string {
  const now = new Date();
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()))
    .toISOString()
    .slice(0, 10);
}

function validateIsinClient(s: string): string | null {
  if (s === "") return null;
  if (!/^[A-Z]{2}[A-Z0-9]{9}[0-9]$/.test(s)) return "isin_invalid_format";
  // Checksum check delegated to backend — format check is sufficient on the client
  return null;
}

function toNumOrNull(s: string): number | null {
  const t = s.trim();
  if (t === "") return null;
  const n = Number(t.replace(",", "."));
  return Number.isFinite(n) ? n : null;
}

export function InvestmentForm({
  mode,
  initial,
  accounts,
  onSubmit,
  submitting,
  serverError,
}: Props) {
  const { t } = useTranslation();
  const [name, setName] = useState(initial?.name ?? "");
  const [symbol, setSymbol] = useState(initial?.symbol ?? "");
  const [kind, setKind] = useState<InvestmentKind>(initial?.kind ?? "AKTIE");
  const [portfolio, setPortfolio] = useState<Portfolio>(initial?.portfolio ?? "PRIVATE");
  const [investmentCategory, setInvestmentCategory] = useState<InvestmentCategory | null>(
    initial?.investmentCategory ?? null,
  );
  const [currency, setCurrency] = useState<CreateInvestmentInput["currency"]>(
    (initial?.currency as CreateInvestmentInput["currency"]) ?? "EUR",
  );
  const [quantity, setQuantity] = useState(
    initial && isBalanceInvestmentKind(initial.kind) ? "1" : (initial?.quantity ?? ""),
  );
  const [purchasePrice, setPurchasePrice] = useState(
    initial && isBalanceInvestmentKind(initial.kind)
      ? "0,00"
      : initial
        ? formatEuroDe(initial.purchasePriceCents)
        : "",
  );
  const [purchaseDate, setPurchaseDate] = useState(initial?.purchaseDate ?? todayIso());
  const [accountId, setAccountId] = useState(initial?.accountId ?? "");
  const [notes, setNotes] = useState(initial?.notes ?? "");
  const [isin, setIsin] = useState<string>(initial?.isin ?? "");
  const [isinError, setIsinError] = useState<string | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  const [p2pSymbolChoice, setP2pSymbolChoice] = useState<string>(
    initial?.kind === "P2P" ? initialP2pChoice(initial.symbol) : "MONEFIT_SMARTSAVER",
  );
  const [growthEstimateStr, setGrowthEstimateStr] = useState(
    initial?.growthEstimatePercent?.toString() ?? "",
  );
  const [payoutYieldStr, setPayoutYieldStr] = useState(
    initial?.payoutYieldPercent?.toString() ?? "",
  );
  const [valuationDeltaStr, setValuationDeltaStr] = useState(
    initial?.valuationDeltaPercent?.toString() ?? "",
  );

  const isIsinApplicable = kind !== "KRYPTO" && !isBalanceInvestmentKind(kind);

  function symbolHint(): string {
    switch (kind) {
      case "KRYPTO":
        return t("investments.form.symbol_hint.krypto");
      case "AKTIE":
      case "ETF":
      case "ROHSTOFFE":
        return t("investments.form.symbol_hint.equity");
      default:
        return t("investments.form.symbol_hint.manual");
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLocalError(null);

    // Bei transaktionalen Positionen sind quantity + purchasePriceCents abgeleitet
    // (runningShares × runningAvgCost aus den BUYs). Sie zurückzusenden riskiert
    // VALIDATION_ERROR — z.B. FP-Drift im quantity-String oder PurchasePrice=0
    // bei Seed-Werten. Beide Felder daher in Edit-Mode mit isTransactional aus
    // dem Patch raushalten.
    const isTransactional = mode === "edit" && initial?.isTransactional === true;

    // For balance-Kinds (P2P/BETEILIGUNG), price is always 0 (sentinel value) — skip parse/validation.
    let priceCents: number;
    if (isBalanceInvestmentKind(kind)) {
      priceCents = 0;
    } else if (isTransactional) {
      priceCents = initial?.purchasePriceCents ?? 0;
    } else {
      try {
        priceCents = parseEuroDe(purchasePrice);
      } catch {
        setLocalError(t("investments.errors.invalid_amount"));
        return;
      }
    }

    const isinClientError = validateIsinClient(isin);
    if (isinClientError) {
      setIsinError(isinClientError);
      return;
    }
    const base: CreateInvestmentInput = {
      name: name.trim(),
      symbol: symbol.trim(),
      kind,
      currency,
      quantity: isBalanceInvestmentKind(kind) ? "1" : quantity.trim(),
      purchasePriceCents: priceCents,
      purchaseDate,
      isin: isin === "" ? null : isin,
      portfolio,
      investmentCategory,
    };
    if (accountId) base.accountId = accountId;
    if (notes.trim()) base.notes = notes.trim();

    // Renditebruecke-Overrides gehen nur im Edit-Mode mit — bei einer neuen
    // Position gibt es noch keinen sinnvollen Bezug dafuer (Fieldset ist dort
    // nicht sichtbar), also gemeinsam mit dem Rest atomar nur im PATCH-Pfad.
    const overrides = {
      growthEstimatePercent: toNumOrNull(growthEstimateStr),
      payoutYieldPercent: toNumOrNull(payoutYieldStr),
      valuationDeltaPercent: toNumOrNull(valuationDeltaStr),
    };

    if (isTransactional) {
      // Quantity + purchasePriceCents aus dem Patch raushalten — Backend leitet
      // die aus den Transaktionen ab, eigene Werte wuerden mit FP-Drift kollidieren
      // oder als 0 die NON_P2P-Validation triggern.
      const patch: UpdateInvestmentInput = {
        name: base.name,
        symbol: base.symbol,
        kind: base.kind,
        purchaseDate: base.purchaseDate,
        isin: base.isin,
        portfolio: base.portfolio,
        investmentCategory: base.investmentCategory,
        ...overrides,
      };
      if (base.accountId !== undefined) patch.accountId = base.accountId;
      if (base.notes !== undefined) patch.notes = base.notes;
      await onSubmit(patch);
      return;
    }
    if (mode === "edit") {
      const patch: UpdateInvestmentInput = { ...base, ...overrides };
      await onSubmit(patch);
      return;
    }
    await onSubmit(base);
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <label className="block">
        <span className="text-sm font-medium">{t("investments.form.name")}</span>
        <input
          className="block mt-1 w-full border rounded px-2 py-1"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          maxLength={120}
        />
      </label>
      <label className="block">
        <span className="text-sm font-medium">{t("investments.form.kind")}</span>
        <select
          className="block mt-1 w-full border rounded px-2 py-1"
          value={kind}
          onChange={(e) => {
            const nextKind = e.target.value as InvestmentKind;
            const wasBalance = isBalanceInvestmentKind(kind);
            const isBalance = isBalanceInvestmentKind(nextKind);
            setKind(nextKind);
            if (nextKind === "KRYPTO" || isBalance) {
              setIsin("");
              setIsinError(null);
            }
            if (isBalance) {
              setQuantity("1");
              setPurchasePrice("0,00");
            } else if (wasBalance) {
              // Wechsel von balance-Kind weg → Sentinel-Werte loeschen
              setQuantity("");
              setPurchasePrice("");
            }
          }}
        >
          {KINDS.map((k) => (
            <option key={k} value={k}>
              {t(`investments.kind.${k}`)}
            </option>
          ))}
        </select>
      </label>
      <label className="block">
        <span className="text-sm font-medium">{t("investments.form.portfolio")}</span>
        <select
          className="block mt-1 w-full border rounded px-2 py-1"
          value={portfolio}
          onChange={(e) => setPortfolio(e.target.value as Portfolio)}
        >
          <option value="PRIVATE">{t("investments.portfolio.PRIVATE")}</option>
          <option value="OTTERFOLIO">{t("investments.portfolio.OTTERFOLIO")}</option>
        </select>
      </label>
      <label className="block">
        <span className="text-sm font-medium">{t("investments.category.label")}</span>
        <select
          className="block mt-1 w-full border rounded px-2 py-1"
          value={investmentCategory ?? ""}
          onChange={(e) => {
            const v = e.target.value;
            setInvestmentCategory(v === "" ? null : (v as InvestmentCategory));
          }}
        >
          <option value="">{t("investments.category.none")}</option>
          <option value="FELS">{t("investments.category.FELS")}</option>
          <option value="RAKETE">{t("investments.category.RAKETE")}</option>
        </select>
      </label>

      {/* Symbol field — P2P-Dropdown, BETEILIGUNG-Hinweisinput, sonst Free-Input */}
      {kind === "P2P" ? (
        <div className="space-y-1">
          <label htmlFor="p2p-platform" className="text-sm font-medium">
            {t("investments.form.p2p_symbol_label")}
          </label>
          <select
            id="p2p-platform"
            value={p2pSymbolChoice}
            onChange={(e) => {
              const v = e.target.value;
              setP2pSymbolChoice(v);
              if (v !== "OTHER") setSymbol(v);
              else setSymbol("");
            }}
            className="border rounded px-2 py-1 text-sm w-full"
          >
            <option value="MONEFIT_SMARTSAVER">Monefit SmartSaver</option>
            <option value="OTHER">{t("investments.form.p2p_symbol_other")}</option>
          </select>
          {p2pSymbolChoice === "OTHER" && (
            <input
              type="text"
              value={symbol}
              onChange={(e) => setSymbol(e.target.value)}
              placeholder="z.B. PRIVATKREDIT_FREUND"
              className="border rounded px-2 py-1 text-sm w-full"
              required
            />
          )}
        </div>
      ) : kind === "BETEILIGUNG" ? (
        <div className="space-y-1">
          <label htmlFor="beteiligung-symbol" className="text-sm font-medium">
            {t("investments.form.beteiligung_symbol_label", {
              defaultValue: "Kürzel/Bezeichnung der Beteiligung",
            })}
          </label>
          <input
            id="beteiligung-symbol"
            type="text"
            value={symbol}
            onChange={(e) => setSymbol(e.target.value)}
            placeholder="z.B. BUERGERWIND_HOHE_MARK"
            className="border rounded px-2 py-1 text-sm w-full"
            required
            maxLength={40}
          />
          <span className="text-xs" style={{ color: "var(--muted)" }}>
            {t("investments.form.beteiligung_symbol_hint", {
              defaultValue: "Frei wählbar — wird nur zur Identifikation genutzt, kein Kursabruf.",
            })}
          </span>
        </div>
      ) : (
        <label className="block">
          <span className="text-sm font-medium">{t("investments.form.symbol")}</span>
          <input
            className="block mt-1 w-full border rounded px-2 py-1"
            value={symbol}
            onChange={(e) => setSymbol(e.target.value)}
            required
            maxLength={40}
          />
          <span className="text-xs text-slate-500">{symbolHint()}</span>
        </label>
      )}

      <div className="space-y-1">
        <label htmlFor="isin" className="text-sm font-medium">
          {t("investments.form.isin_label")}
        </label>
        <input
          id="isin"
          type="text"
          value={isin}
          onChange={(e) => setIsin(e.target.value.toUpperCase())}
          onBlur={() => setIsinError(validateIsinClient(isin))}
          disabled={!isIsinApplicable}
          title={!isIsinApplicable ? t("investments.form.isin_not_applicable") : undefined}
          maxLength={12}
          className="border rounded px-2 py-1 text-sm w-full disabled:bg-slate-100"
        />
        {isinError && <p className="text-xs text-red-600">{t(`investments.form.${isinError}`)}</p>}
      </div>
      <label className="block">
        <span className="text-sm font-medium">{t("investments.form.currency")}</span>
        <select
          className="block mt-1 w-full border rounded px-2 py-1"
          value={currency}
          onChange={(e) => setCurrency(e.target.value as CreateInvestmentInput["currency"])}
        >
          {CURRENCIES.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </label>

      {!isBalanceInvestmentKind(kind) && !initial?.isTransactional && (
        <>
          <label className="block">
            <span className="text-sm font-medium">{t("investments.form.quantity")}</span>
            <input
              className="block mt-1 w-full border rounded px-2 py-1"
              inputMode="decimal"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              required
              placeholder="10.5"
            />
          </label>
          <label className="block">
            <span className="text-sm font-medium">{t("investments.form.purchase_price")}</span>
            <input
              className="block mt-1 w-full border rounded px-2 py-1"
              inputMode="decimal"
              value={purchasePrice}
              onChange={(e) => setPurchasePrice(e.target.value)}
              required
              placeholder="150,00"
            />
          </label>
        </>
      )}

      {initial?.isTransactional && (
        <p className="text-xs text-slate-500">
          {t("investments.transaction.form_transactional_note")}
        </p>
      )}

      <label className="block">
        <span className="text-sm font-medium">{t("investments.form.purchase_date")}</span>
        <input
          type="date"
          className="block mt-1 w-full border rounded px-2 py-1"
          value={purchaseDate}
          max={todayIso()}
          onChange={(e) => setPurchaseDate(e.target.value)}
          required
        />
      </label>
      <label className="block">
        <span className="text-sm font-medium">{t("investments.form.account")}</span>
        <select
          className="block mt-1 w-full border rounded px-2 py-1"
          value={accountId}
          onChange={(e) => setAccountId(e.target.value)}
        >
          <option value="">{t("investments.form.account_none")}</option>
          {accounts
            .filter((a) => a.isActive)
            .map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
        </select>
      </label>
      <label className="block">
        <span className="text-sm font-medium">{t("investments.form.notes")}</span>
        <textarea
          className="block mt-1 w-full border rounded px-2 py-1"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          maxLength={500}
          rows={3}
        />
      </label>

      {mode === "edit" && (
        <fieldset className="border rounded p-3 mt-4 space-y-2">
          <legend className="text-sm font-medium px-1">
            {t("otterfolio.renditebruecke.overrides", {
              defaultValue: "Renditebrücke — Deine Werte",
            })}
          </legend>
          <label className="block">
            <span className="text-sm font-medium">
              {t("otterfolio.renditebruecke.growthEstimateLabel", {
                defaultValue: "Gewinnwachstum (%/a)",
              })}
            </span>
            <input
              className="block mt-1 w-full border rounded px-2 py-1"
              value={growthEstimateStr}
              onChange={(e) => setGrowthEstimateStr(e.target.value)}
              inputMode="decimal"
              placeholder="z.B. 8"
            />
          </label>
          <label className="block">
            <span className="text-sm font-medium">
              {t("otterfolio.renditebruecke.payoutYieldLabel", {
                defaultValue: "Ausschüttung (Dividende + Buyback, %/a)",
              })}
            </span>
            <input
              className="block mt-1 w-full border rounded px-2 py-1"
              value={payoutYieldStr}
              onChange={(e) => setPayoutYieldStr(e.target.value)}
              inputMode="decimal"
              placeholder="z.B. 3.5"
            />
          </label>
          <label className="block">
            <span className="text-sm font-medium">
              {t("otterfolio.renditebruecke.valuationDeltaLabel", {
                defaultValue: "Bewertungs-Delta (%/a)",
              })}
            </span>
            <input
              className="block mt-1 w-full border rounded px-2 py-1"
              value={valuationDeltaStr}
              onChange={(e) => setValuationDeltaStr(e.target.value)}
              inputMode="decimal"
              placeholder="z.B. -1.5 oder 2"
            />
          </label>
        </fieldset>
      )}

      {(localError ?? serverError) && (
        <div className="text-red-700 text-sm">{localError ?? serverError}</div>
      )}
      <button
        type="submit"
        className="px-4 py-2 bg-blue-600 text-white rounded"
        disabled={submitting}
      >
        {submitting
          ? t("common.submitting")
          : mode === "create"
            ? t("common.create")
            : t("common.save")}
      </button>
    </form>
  );
}
